<script setup>
import { computed, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useWgptAuditTrail } from 'dashboard/composables/wgpt/wgptAuditTrail';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import AuditTrails from './AutomationEditorAuditTrails.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const { isFeatureEnabled } = useWgptAuditTrail();

const form = ref({
  name: props.modelValue.name || '',
  description: props.modelValue.description || '',
  // Add other fields as needed
});

const rules = {
  name: { required },
  description: {},
  // Add other field validations as needed
};

const v$ = useVuelidate(rules, form);

const nameErrorMessage = computed(() => {
  if (v$.value.name.$error) {
    if (v$.value.name.required) {
      return t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.SETTINGS.FORM.NAME.ERROR');
    }
  }
  return '';
});

watch(
  form,
  newValue => {
    emit('update:modelValue', { ...props.modelValue, ...newValue });
  },
  { deep: true }
);

const onSubmit = () => {
  v$.value.$validate();
};
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        <span class="truncate">
          {{ $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.SETTINGS.TITLE') }}
        </span>
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
      <Input
        v-model="form.name"
        :disabled="disabled"
        :label="$t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.SETTINGS.FORM.NAME.LABEL')"
        :message="nameErrorMessage"
        :message-type="nameErrorMessage ? 'error' : 'info'"
        @input="v$.name.$touch"
        @blur="onSubmit"
      />

      <TextArea
        v-model="form.description"
        :disabled="disabled"
        :label="
          t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.SETTINGS.FORM.DESCRIPTION.LABEL')
        "
        @blur="onSubmit"
      />
      <!-- Add other form fields as needed -->
    </form>

    <template v-if="isFeatureEnabled">
      <div class="border-t border-n-weak my-4" />

      <AuditTrails />
    </template>
  </div>
</template>
